import React, { useMemo } from "react";
import { getActiveFestival, getPreviewFestival } from "@/lib/festivals";

/**
 * v1.27.95 — Festival greeting banner.
 * Renders NOTHING outside the [festival_date − 4, festival_date + 1] window.
 * Applies subtle festive gradient + emoji + tagline. Contains no data —
 * mount it at the very top of a dashboard so tables / KPIs stay untouched.
 *
 * URL preview: append `?festival=diwali` (or any key from FESTIVALS) to test
 * any theme without changing the system date.
 */
export default function FestivalBanner({ subtitle }) {
    const active = useMemo(() => getPreviewFestival() || getActiveFestival(), []);
    if (!active) return null;

    const bgStyle = {
        background: `linear-gradient(90deg, ${active.from} 0%, ${active.via} 50%, ${active.to} 100%)`,
    };

    return (
        <div
            data-testid={`festival-banner-${active.key}`}
            role="status"
            aria-label={active.greeting}
            className={`relative overflow-hidden rounded-2xl px-4 py-3 shadow-md ring-1 ring-black/5 ${active.text}`}
            style={bgStyle}
        >
            {/* Subtle decorative glows — pure CSS, no images. */}
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/15 blur-2xl" aria-hidden />
            <div className="pointer-events-none absolute -bottom-8 -left-4 h-24 w-24 rounded-full bg-white/10 blur-2xl" aria-hidden />

            <div className="relative flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="text-2xl leading-none drop-shadow-sm" aria-hidden>{active.emoji}</span>
                    <div>
                        <div className={`text-[10px] font-bold uppercase tracking-[0.24em] opacity-80 ${active.accent}`}>
                            Festival Greetings
                        </div>
                        <div className="font-display text-lg font-black tracking-tight sm:text-xl">
                            {active.greeting}
                        </div>
                        <div className={`text-xs opacity-90 ${active.accent}`}>
                            {subtitle || active.tagline}
                        </div>
                    </div>
                </div>
                {active._preview && (
                    <span
                        data-testid="festival-banner-preview-chip"
                        className="rounded-full bg-black/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                    >
                        Preview
                    </span>
                )}
            </div>
        </div>
    );
}
